import { __rest } from "tslib";
import { Command } from 'cmdk';
import React, { createContext, useContext } from 'react';
import { CheckIcon, ChevronBottomIcon, SearchIcon, } from '@marvelapp/ballpark-icons';
import { BtwButton } from './BtwButton';
import { BtwCheckbox } from './BtwCheckbox';
import { BtwInput } from './BtwInput';
import { BtwPopover } from './BtwPopover';
import { BtwText } from './BtwText';
import { cn } from './utils';
const ComboboxContext = createContext(undefined);
const useComboboxState = () => {
    const comboboxState = useContext(ComboboxContext);
    if (comboboxState === undefined) {
        throw new Error('useComboboxState used outside of Provider');
    }
    return comboboxState;
};
/**
 * Root
 */
function Root(props) {
    const { children } = props, rest = __rest(props, ["children"]);
    const [open, onOpenChange] = React.useState(false);
    return (React.createElement(BtwPopover.Root, Object.assign({ open: open, onOpenChange: onOpenChange }, rest),
        React.createElement(ComboboxContext.Provider, { value: { open, onOpenChange } }, children)));
}
function Trigger(props) {
    const { children, className, hasPlaceholder, truncate = true } = props, rest = __rest(props, ["children", "className", "hasPlaceholder", "truncate"]);
    return (React.createElement(BtwPopover.Trigger, { asChild: true },
        React.createElement(BtwButton, Object.assign({ className: cn('justify-between', {
                'font-normal text-gray-500': hasPlaceholder,
            }, className), trailingIcon: React.createElement(ChevronBottomIcon, null) }, rest),
            React.createElement("span", { className: cn({ 'min-w-0 truncate': truncate }) }, children))));
}
/**
 * Separator
 */
function Separator(props) {
    const { className } = props, rest = __rest(props, ["className"]);
    return (React.createElement(Command.Separator, Object.assign({ className: cn('h-px', 'w-full', 'bg-gray-200', className) }, rest)));
}
function Item(props) {
    const { check = 'icon', children, onSelect, selected, shouldClose = true } = props, rest = __rest(props, ["check", "children", "onSelect", "selected", "shouldClose"]);
    const { onOpenChange } = useComboboxState();
    return (React.createElement(BtwButton, { asChild: true, className: "font-13 h-auto justify-start py-2 pl-3 pr-3 text-left font-medium [&>.inner-icon]:ml-auto [&>.inner-icon]:flex-none", variant: "ghost", width: "full", trailingIcon: selected && check === 'icon' ? React.createElement(CheckIcon, null) : undefined },
        React.createElement(Command.Item, Object.assign({ onSelect: (value) => {
                if (onSelect) {
                    onSelect(value);
                }
                if (shouldClose) {
                    onOpenChange(false);
                }
            } }, rest),
            check === 'checkbox' && (React.createElement("div", { 
                // We should potentially handle this spacing alignment on <Button /> component
                // and make it per size, but it seems fine for now since we only have this button size
                // that uses a checkbox inside
                className: "-ml-0.5" },
                React.createElement(BtwCheckbox, { checked: selected, 
                    // The selection logic is handled by the parent <Command.Item /> component
                    size: "sm" }))),
            React.createElement("span", { className: "flex-1 truncate" }, children))));
}
/**
 * Content
 */
function Content(props) {
    const { children, placeholder = 'Search for options…', width = 'trigger' } = props, rest = __rest(props, ["children", "placeholder", "width"]);
    return (React.createElement(BtwPopover.Content, Object.assign({ width: width, asChild: true }, rest),
        React.createElement(Command, null,
            React.createElement("header", { className: "border-b border-gray-200 p-2.5" },
                React.createElement(BtwInput.Root, { leadingIcon: React.createElement(SearchIcon, null) },
                    React.createElement(BtwInput.Field, { asChild: true, id: "search" },
                        React.createElement(Command.Input, { placeholder: placeholder })))),
            React.createElement(Command.List, { className: "p-1.5 [&>div]:flex [&>div]:w-full [&>div]:flex-col [&>div]:gap-0.5" }, children))));
}
/**
 * Empty state
 */
function Empty(props) {
    const { className, children = React.createElement(React.Fragment, null, "No matches found\u2026") } = props, rest = __rest(props, ["className", "children"]);
    return (React.createElement(BtwText, { align: "center", asChild: true, className: cn('p-4', className), size: "sm" },
        React.createElement(Command.Empty, Object.assign({}, rest), children)));
}
const ComboboxV2 = {
    Content,
    Empty,
    Item,
    Root,
    Separator,
    Trigger,
};
export { ComboboxV2 };
